import { useEffect } from "react";
import { BreadCrumb } from "primereact/breadcrumb";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MeaPage from "../../components/MeaPage";
import MeaLink from "../../components/MeaLink";
import { useAuthContext } from "../../hooks/useAuthContext";
import trainingsAPI from "../../services/trainings";
import accountsAPI from "../../services/accounts";
import SlotChangeRequestsListComponent from "../trainings/slot-change-requests/components/SlotChangeRequestsListComponent";
import SlotChangeProposalsComponent from "../trainings/slot-change-requests/components/SlotChangeProposalsComponent";
import { PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS } from "../../permissions";

const SchoolSlotChangeRequestsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const {
    hasPermission,
    auth,
    currentMembership,
    updateCurrentMembershipPreferences,
  } = useAuthContext();

  const schoolId = params.schoolId || currentMembership?.school?.id;
  const selectedTrainingId = params.trainingId || currentMembership?.preferences?.last_visited_training;
  const accessToken = auth?.access_token || "";
  const isProposalsPage = location.pathname.endsWith("/proposals");

  const trainingsQuery = trainingsAPI.useGetList(
    schoolId,
    accessToken,
    1,
    1000,
    ""
  );
  const trainings = trainingsQuery?.data?.data || [];

  useEffect(() => {
    if (!schoolId || trainings.length === 0) {
      return;
    }

    if (!selectedTrainingId) {
      const fallbackTrainingId = currentMembership?.preferences?.last_visited_training || trainings[0].id;
      if (fallbackTrainingId) {
        navigate(`/school/${schoolId}/admin/slot-change-requests/${fallbackTrainingId}`, { replace: true });
      }
      return;
    }

    const hasSelectedTraining = trainings.some((training) => training.id === selectedTrainingId);
    if (!hasSelectedTraining) {
      const fallbackTrainingId = currentMembership?.preferences?.last_visited_training || trainings[0].id;
      if (fallbackTrainingId) {
        navigate(`/school/${schoolId}/admin/slot-change-requests/${fallbackTrainingId}`, { replace: true });
      }
    }
  }, [schoolId, selectedTrainingId, trainings, currentMembership, navigate]);

  const selectedTraining = trainings.find((training) => training.id === selectedTrainingId) || null;

  const handleTrainingChange = async (nextTrainingId) => {
    if (!nextTrainingId) {
      return;
    }

    try {
      if (currentMembership?.id) {
        const updatedPrefs = await accountsAPI.UpdateMemberPreferences(
          auth?.access_token,
          currentMembership.id,
          nextTrainingId
        );

        if (updatedPrefs) {
          updateCurrentMembershipPreferences(updatedPrefs);
        } else {
          const fallbackPrefs = currentMembership?.preferences
            ? { ...currentMembership.preferences, last_visited_training: nextTrainingId }
            : { last_visited_training: nextTrainingId };
          updateCurrentMembershipPreferences(fallbackPrefs);
        }
      }
    } catch (err) {
      console.error("Failed to save training preference", err);
    }

    navigate(
      `/school/${schoolId}/admin/slot-change-requests/${nextTrainingId}${isProposalsPage ? "/proposals" : ""}`,
      { replace: true }
    );
  };

  const sectionOptions = [
    { label: "Inscriptions", value: "candidates" },
    { label: "Paiements", value: "payments" },
    { label: "Campagnes d'e-mails", value: "campaigns" },
    { label: "Gestion de l'espace famille", value: "settings" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Changements de créneau", value: "slot-change-requests" },
    { label: "Suivi des observations", value: "student-observations" },
  ];

  const handleSectionChange = (nextSection) => {
    if (!nextSection || nextSection === "slot-change-requests") {
      return;
    }
    if (nextSection === "avis-passage" || nextSection === "student-observations") {
      navigate(`/school/${schoolId}/admin/${nextSection}/${selectedTrainingId}`);
      return;
    }
    navigate(`/school/${schoolId}/admin/registrations/${selectedTrainingId}/${nextSection}`);
  };

  const breadcrumb = [
    {
      template: () => (
        <MeaLink to={`/school/${schoolId}/admin`} icon="pi pi-home" isRipple={true}>
          {currentMembership?.school?.name || "Accueil"}
        </MeaLink>
      ),
    },
    { label: "Changements de créneau" },
  ];

  if (selectedTraining) {
    breadcrumb.push({ label: selectedTraining.name });
  }

  const customWorkspaceBar = (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value="slot-change-requests"
          options={sectionOptions}
          onChange={(e) => handleSectionChange(e.value)}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={selectedTrainingId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={(e) => handleTrainingChange(e.value)}
          placeholder={selectedTraining?.name || "Sélectionner une formation"}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
        />
      </div>
    </div>
  );

  if (!hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.LIST)) {
    return (
      <MeaPage
        title="Changements de créneau"
        breadcrumb={<BreadCrumb model={breadcrumb} />}
      >
        <Message
          severity="warn"
          text="Vous n'avez pas accès à la liste des demandes de changement de créneau."
        />
      </MeaPage>
    );
  }

  if (isProposalsPage && !hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.APPLY)) {
    return (
      <MeaPage
        title="Propositions d'affectation"
        breadcrumb={<BreadCrumb model={breadcrumb} />}
      >
        <Message
          severity="warn"
          text="Vous n'avez pas accès aux propositions de changement de créneau."
        />
      </MeaPage>
    );
  }

  if (!selectedTrainingId || !selectedTraining) {
    return (
      <MeaPage
        title="Changements de créneau"
        breadcrumb={<BreadCrumb model={breadcrumb} />}
      >
        <Message severity="info" text="Aucune formation sélectionnée." />
      </MeaPage>
    );
  }

  return (
    <MeaPage
      title={isProposalsPage ? "Propositions d'affectation" : "Changements de créneau"}
      subtitle={
        isProposalsPage
          ? `Vérifiez les meilleures affectations puis approuvez les changements sélectionnés — ${selectedTraining.name}`
          : `Gérer les demandes de changement de classe sur un autre créneau du même niveau — ${selectedTraining.name}`
      }
      breadcrumb={customWorkspaceBar}
    >
      {isProposalsPage ? (
        <SlotChangeProposalsComponent />
      ) : (
        <SlotChangeRequestsListComponent
          schoolId={schoolId}
          trainingId={selectedTrainingId}
          showAddButton={true}
          showFilterButton={true}
          showFilter={true}
        />
      )}
    </MeaPage>
  );
};

export default SchoolSlotChangeRequestsPage;
